import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  jsonLd?: object | object[];
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogTitle,
  ogDescription,
  ogUrl,
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  jsonLd,
}: SEOProps) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    // Update title
    if (title) {
      document.title = title;
    }

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Update link tags
    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      
      element.href = href;
    };

    // Basic meta tags
    if (description) updateMetaTag('description', description);
    if (keywords) updateMetaTag('keywords', keywords);

    // Canonical URL
    if (canonical) {
      updateLinkTag('canonical', canonical.startsWith('http') ? canonical : `${siteUrl}${canonical}`);
    }

    // Open Graph tags
    updateMetaTag('og:type', ogType, true);
    if (ogTitle) updateMetaTag('og:title', ogTitle, true);
    if (ogDescription) updateMetaTag('og:description', ogDescription, true);
    if (ogUrl) {
      updateMetaTag('og:url', ogUrl.startsWith('http') ? ogUrl : `${siteUrl}${ogUrl}`, true);
    }

    // Twitter tags
    updateMetaTag('twitter:card', twitterCard);
    if (twitterTitle) updateMetaTag('twitter:title', twitterTitle);
    if (twitterDescription) updateMetaTag('twitter:description', twitterDescription);

    // JSON-LD structured data
    if (jsonLd) {
      // Remove existing JSON-LD scripts added by this component
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-dynamic="true"]');
      existingScripts.forEach(script => script.remove());

      // Add new JSON-LD
      const jsonLdArray = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      
      jsonLdArray.forEach((data) => {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-dynamic', 'true');
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }

    // Update last-modified date
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);
  }, [title, description, keywords, canonical, ogType, ogTitle, ogDescription, ogUrl, twitterCard, twitterTitle, twitterDescription, jsonLd]);
}
